import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useState } from 'react';

interface StorageStateOptions<T> {
  defaultValue?: T;
  isValid?: (value: T) => boolean;
}

// Without `defaultValue`:
function useStorageState<T>(
  storage: Storage,
  key: string,
  options?: Omit<StorageStateOptions<T>, 'defaultValue'>,
): [T | undefined, Dispatch<SetStateAction<T | undefined>>];

// With `defaultValue`:
function useStorageState<T>(
  storage: Storage,
  key: string,
  options: StorageStateOptions<T> & { defaultValue: T },
): [T, Dispatch<SetStateAction<T>>];

function useStorageState<T>(
  storage: Storage,
  key: string,
  {
    defaultValue,
    isValid,
  }: { defaultValue?: T; isValid?: (value: T) => boolean } = {},
): [T, Dispatch<SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => {
    const storedValue = storage.getItem(key);

    if (storedValue == null) return defaultValue;

    try {
      const parsedValue = JSON.parse(storedValue);

      // Validate the parsed stored value. If it's invalid, return the default
      //   value (if any).
      if (isValid && !isValid(parsedValue)) throw Error('invalid stored value');
      return parsedValue;
    } catch (thrown) {
      storage.removeItem(key);
      return defaultValue;
    }
  });

  useEffect(() => {
    if (value == null) {
      storage.removeItem(key);
    } else {
      storage.setItem(key, JSON.stringify(value));
    }
  }, [key, storage, value]);

  return [value, setValue];
}

export default useStorageState;
